import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { SwapVert } from '@mui/icons-material';

import { CONFIG } from '@frontend/config';
import { client } from '@frontend/features/common/services';
import {
  PricedSearchResult,
  SearchEnvelope,
  TransferPointPayload,
  isPickupInPast,
  isReturnBeforeDeparture,
  ruteroSearchKey,
  usePlaceCoords,
  useSearchTransfers,
} from '../queries';
import {
  UrlParams,
  cleanParams,
  enumParam,
  listParam,
  numParam,
  serializeList,
  isIncompleteSearch,
  useSearchUrlState,
} from '../useSearchUrlState';
import { committedPlaceCoordsIndex } from '../placeCoords';
import { usePlacesEnabled } from '../usePlacesEnabled';
import {
  TransferPointAutocomplete,
  TransferPointSelection,
} from './TransferPointAutocomplete';
import {
  TransferPointControl,
  TransferPointType,
  shouldShowPointModeToggle,
  transferPointControl,
  transferPointValue,
  transferPointsReady,
} from '../transferPointPicker';
import {
  PointMode,
  PointState,
  isPointValid,
  needsPostalCompletion,
  pointFromParams,
  pointToParams,
  toPayload,
} from '../transferPointState';
import { describeTransfers } from '../describeSearch';
import {
  SearchBar,
  SearchDivider,
  SearchField,
  SearchRow,
  SearchSection,
} from './SearchBar';
import {
  SEARCH_DATE_FIELD_WIDTH,
  SEARCH_FIELD_HALF_SHARE,
  SEARCH_FIELD_NARROW_SHARE,
  SEARCH_PASSENGER_FIELD_WIDTH,
  SEARCH_FIELD_WIDE_SHARE,
} from '../searchFieldLayout';
import { RecentSearches } from './RecentSearches';
import { TransferOfferCard } from './TransferOfferCard';
import {
  FilterChip,
  FilterGroup,
  ResultFiltersBar,
} from './ResultFiltersBar';
import { ProviderStatusStrip } from './ProviderStatusStrip';
import {
  transferDraftRouteEnd,
  transferPointKindFromParams,
  transferPointKindParam,
  transferRoute,
  transferRouteTouchesAirport,
  EMPTY_TRANSFER_FILTERS,
  TransferFilters,
  TransferOffer,
  TransferSort,
  categoryKey,
  categoryLabel,
  filterTransfers,
  runTransferRequote,
  sortTransfers,
  transferFacetCount,
  transferOfferListKey,
  transferPointNameParam,
  transferScheduleValidation,
} from '../transfers';
import {
  TRANSFER_PASSENGER_RULES,
  searchPassengersToParam,
  transferPassengersFromParams,
  validateSearchPassengers,
} from '../searchPassengers';
import { SearchPassengerPicker } from './SearchPassengerPicker';
import { reconcileSearchEndDate } from '../searchDates';
import { SearchDatePicker } from './SearchDatePicker';
import { BookingModal } from './BookingModal';
import { useBookingCaseContext } from './BookingCaseContext';
import {
  committedParty,
  transferCommittedBookingContext,
  transferRouteMapPoints,
} from '../committedBookingContext';
import { quoteSearchIdentity, SelectedTravelQuote } from '../selectedQuote';
import {
  captureSelectableSearchQuote,
  searchFormDiverged,
  shouldRefetchExpiredIdenticalSearch,
} from '../searchResultAuthority';
import { useFareCountdown } from '../useFareCountdown';
import { useSearchResultAuthority } from '../useSearchResultAuthority';
import { SearchResultGenerationStatus } from './SearchResultGenerationStatus';
import { ResultsSkeleton } from './ResultsSkeleton';
import { ActiveSearchSummary } from './ActiveSearchSummary';
import { searchRouteMapKey } from '../routeMap';
import { SearchRouteMap } from './SearchRouteMap';
import { TransferLuggagePicker } from './TransferLuggagePicker';
import { emptyTransferLuggage, transferLuggageFromParams, transferLuggageSelection, transferLuggageSummary, transferLuggageToParams } from '../transferLuggage';
import { transferSearchPayloadFromParams as payloadFromParams } from '../transferSearchPayload';

const CAPACITY_FILTERS = [4, 6, 8];

interface PointFieldsProps {
  label: string;
  point: PointState;
  onChange: (point: PointState) => void;
  /** Control extra junto al título (el botón de invertir origen y destino). */
  action?: ReactNode;
  /** Reenvía si este punto tiene un resolve de geocode en vuelo. */
  onResolvingChange?: (resolving: boolean) => void;
  /** Ver `resetKey` en `TransferPointAutocomplete`: descarta el resolve en vuelo. */
  resetKey: number;
  /**
   * `undefined` (cargando) cuenta como apagado, mismo criterio que el resto
   * del epic #2167 — nunca mostrar el buscador de direcciones sin
   * confirmación positiva del setting.
   */
  placesEnabled: boolean;
}

/**
 * El selector de "ubicar por". Solo se dibuja con el setting de lugares
 * APAGADO (`shouldShowPointModeToggle`, #2227): con el setting prendido el
 * tipo del punto sale de lo que el asesor elige en el campo unificado y no
 * hay ningún paso previo; apagado no hay maestro ni resolver que consultar y
 * las dos formas de carga a mano son incompatibles entre sí, así que ahí el
 * tipo hay que declararlo.
 */
function PointModeToggle({
  value,
  onChange,
}: {
  value: PointMode;
  onChange: (value: PointMode) => void;
}) {
  const { t } = useTranslation();

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, next) => {
        if (next) onChange(next);
      }}
      aria-label={t('travelServices.transfers.pointMode')}
      sx={{
        bgcolor: 'background.paper',
        '& .MuiToggleButton-root': {
          borderColor: 'divider',
          color: 'text.secondary',
          fontWeight: 600,
          minHeight: 32,
          px: 1.4,
          textTransform: 'none',
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          },
        },
      }}
    >
      <ToggleButton value="iata" aria-label={t('travelServices.transfers.iataPoint')}>
        {t('travelServices.transfers.iataPoint')}
      </ToggleButton>
      <ToggleButton value="coords" aria-label={t('travelServices.transfers.placePoint')}>
        {t('travelServices.transfers.placePoint')}
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

/**
 * Los datos de un punto del viaje. NO se dibuja como un bloque aparte: emite
 * campos de la barra de búsqueda, para que origen, destino y el resto de los
 * datos vivan en una sola tarjeta y no en tres pedazos sueltos.
 */
function PointFields({
  label,
  point,
  onChange,
  action,
  placesEnabled,
  onResolvingChange,
  resetKey,
}: PointFieldsProps) {
  const { t } = useTranslation();
  const set = (partial: Partial<PointState>) => onChange({ ...point, ...partial });
  /**
   * Qué control dibuja este punto. UNA sola derivación (`transferPointControl`),
   * que reusa las dos reglas que ya existían —el mode-lock de
   * `resolvePlacesPickerMode` y `shouldShowManualCoordsFields`— en vez de
   * repetirlas acá: lo que el formulario exige (`isPointValid`) y lo que la
   * pantalla ofrece para escribirlo tienen que salir de la misma expresión.
   */
  const control: TransferPointControl = transferPointControl({
    mode: point.mode,
    placesEnabled,
    hasIata: Boolean(point.iata),
    hasPlace: Boolean(point.placePoint),
    useManualFields: point.useManualFields,
  });

  /**
   * Lo elegido en el campo unificado DEFINE el tipo del punto: un lugar del
   * maestro lo pone en modo `iata` (viaja como `{kind: 'place'}`), una
   * dirección resuelta en modo `coords` (viaja como `{kind: 'detail'}` con
   * `type: 'H'`, lo único que ese payload admite). Cada rama limpia la otra:
   * los dos submodos son mutuamente excluyentes por contenido, y un resto
   * viejo del otro reconstruiría un punto a medias desde la URL.
   *
   * `type` en la rama de lugar se normaliza igual que lo hacía el selector
   * que este campo reemplaza (`'H'` → `'A'`): no viaja al payload de un
   * lugar, pero sí a la URL, y un punto en modo `iata` con `type: 'H'`
   * describiría un hotel con código de aeropuerto.
   */
  const handleSelection = (selection: TransferPointSelection | null) => {
    if (!selection) {
      set({
        placePoint: null,
        placeKind: undefined,
        iata: '',
        name: '',
        address: '',
        town: '',
        country: '',
        zip: '',
        lat: '',
        lng: '',
      });
      return;
    }
    if (selection.source === 'place') {
      set({
        mode: 'iata',
        placePoint: selection.place,
        placeKind: selection.place.kind,
        iata: '',
        name: '',
        address: '',
        town: '',
        country: '',
        zip: '',
        lat: '',
        lng: '',
      });
      return;
    }
    set({
      mode: 'coords',
      placePoint: null,
      placeKind: undefined,
      iata: '',
      useManualFields: false,
      town: '',
      country: '',
      zip: '',
      ...selection.point,
    });
  };

  const sectionAction = (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      {shouldShowPointModeToggle({ placesEnabled }) && (
        <PointModeToggle
          value={point.mode}
          onChange={(mode) => set({ mode })}
        />
      )}
      {action}
    </Box>
  );

  return (
    <>
      {/* El picker unificado ya dice Origen/Destino en el propio campo: un
          título de sección repetía esa información y forzaba una fila entera
          por punto. Los modos manuales conservan la sección porque emiten
          varios campos y sí necesitan agruparlos. */}
      {control !== 'picker' && (
        <SearchSection label={label} action={sectionAction} />
      )}
      {control === 'picker' ? (
        <>
          <SearchField label={label} width={260} grow>
            <TransferPointAutocomplete
              label={label}
              placeholder={t('travelServices.transfers.pointPlaceholder')}
              value={transferPointValue({
                placePoint: point.placePoint,
                address: point.address,
                name: point.name,
              })}
              onChange={handleSelection}
              onResolvingChange={onResolvingChange}
              resetKey={resetKey}
              // Pasar a carga manual es EMPEZAR OTRO punto, no anotar el mismo
              // de otra forma: lo que hubiera elegido en el campo unificado se
              // limpia, igual que "buscar por nombre" vacía los campos a mano
              // al volver. Sin esto, un lugar elegido antes de que el resolver
              // fallara sobrevive invisible y reaparece como valor del campo al
              // volver del fallback, describiendo un punto que el payload ya no
              // manda.
              onUseManualFallback={() =>
                set({
                  mode: 'coords',
                  useManualFields: true,
                  placePoint: null,
                  // Abandonar el lugar canónico abandona su tipo: si no, un
                  // aeropuerto descartado seguiría rotulando "hora del vuelo"
                  // un punto tipeado a mano.
                  placeKind: undefined,
                  iata: '',
                  town: '',
                  country: '',
                  zip: '',
                })
              }
              width="100%"
            />
          </SearchField>
          {needsPostalCompletion(point) && (
            <Button size="small" onClick={() => set({ useManualFields: true })}>
              {t('travelServices.transfers.completeAddress')}
            </Button>
          )}
          {action && (
            <Box display="flex" alignItems="center" alignSelf="stretch">
              {action}
            </Box>
          )}
        </>
      ) : control === 'iata' ? (
        <SearchField label="IATA" width={110}>
          <TextField
            size="small"
            fullWidth
            label="IATA"
            value={point.iata}
            onChange={(event) =>
              set({
                iata: event.target.value,
                placePoint: null,
                placeKind: undefined,
              })
            }
            inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
          />
        </SearchField>
      ) : (
        <>
          <SearchField label={t('travelServices.transfers.name')} width={170} grow>
            <TextField
              size="small"
              fullWidth
              label={t('travelServices.transfers.name')}
              value={point.name}
              onChange={(event) =>
                set({ name: event.target.value, useManualFields: true })
              }
            />
          </SearchField>
          <SearchDivider />
          <SearchField label={t('travelServices.transfers.address')} width={190} grow>
            <TextField
              size="small"
              fullWidth
              label={t('travelServices.transfers.address')}
              value={point.address}
              onChange={(event) =>
                set({ address: event.target.value, useManualFields: true })
              }
            />
          </SearchField>
          <SearchDivider />
          <SearchField label="Lat" width={100} xsShare={SEARCH_FIELD_HALF_SHARE}>
            <TextField
              size="small"
              fullWidth
              label="Lat"
              value={point.lat}
              onChange={(event) =>
                set({ lat: event.target.value, useManualFields: true })
              }
            />
          </SearchField>
          <SearchField label="Lng" width={100} xsShare={SEARCH_FIELD_HALF_SHARE}>
            <TextField
              size="small"
              fullWidth
              label="Lng"
              value={point.lng}
              onChange={(event) =>
                set({ lng: event.target.value, useManualFields: true })
              }
            />
          </SearchField>
          {/* Salida de vuelta al campo unificado: solo tiene sentido si el
              setting está prendido (si no, esto ES el único camino). Un
              punto ya cargado se vacía a propósito — no hay nada que
              "restaurar" en el buscador, y dejarlo con datos viejos
              confundiría cuál es la fuente de verdad. */}
          {placesEnabled && (
            <SearchField label="" width={170}>
              <Button
                size="small"
                onClick={() =>
                  set({
                    useManualFields: false,
                    name: '',
                    address: '',
                    town: '',
                    country: '',
                    zip: '',
                    lat: '',
                    lng: '',
                  })
                }
              >
                {t('travelServices.transfers.geocode.searchAgain')}
              </Button>
            </SearchField>
          )}
        </>
      )}
      {point.mode === 'coords' && control === 'manual' && (
        <>
          {(['town', 'country', 'zip'] as const).map((field) => (
            <SearchField
              key={field}
              label={t(`travelServices.transfers.${field}`)}
              width={field === 'zip' ? 120 : 160}
            >
              <TextField
                size="small"
                fullWidth
                label={t(`travelServices.transfers.${field}`)}
                value={point[field] ?? ''}
                onChange={(event) => set({ [field]: event.target.value })}
                inputProps={{ maxLength: field === 'zip' ? 15 : 300 }}
              />
            </SearchField>
          ))}
          <Typography variant="caption" color="text.secondary">
            {t('travelServices.transfers.postalAddressHint')}
          </Typography>
        </>
      )}
    </>
  );
}

export function TransfersSearchForm() {
  const { t, i18n } = useTranslation();
  const url = useSearchUrlState({ vertical: 'transfers', dateParam: 'fecha' });
  const queryClient = useQueryClient();
  // `undefined` (cargando) cuenta como apagado — nunca mostrar un picker de
  // lugares sin confirmación positiva (mismo criterio que el resto del epic
  // #2167). Gatea el campo unificado del punto, que es el que consulta las
  // DOS fuentes: el maestro de lugares (#2178) y el resolver de direcciones
  // (#2176) — un solo setting, no dos derivaciones que puedan desalinearse.
  const placesEnabled = usePlacesEnabled() === true;

  // El formulario se INICIALIZA desde la URL (deep link / vuelta al vertical) y
  // después vive en estado local: tipear no puede depender del router.
  const [tripType, setTripType] = useState<'ONEWAYTRIP' | 'ROUNDTRIP'>(() =>
    enumParam(url.params, 'viaje', ['ONEWAYTRIP', 'ROUNDTRIP'] as const, 'ONEWAYTRIP'));
  const [origin, setOrigin] = useState<PointState>(() =>
    pointFromParams(url.params, 'o', 'iata'));
  const [destination, setDestination] = useState<PointState>(() =>
    pointFromParams(url.params, 'd', 'coords'));
  // Un resolve en vuelo en CUALQUIERA de los dos puntos bloquea el swap —
  // ver el comentario en el botón.
  const [originResolving, setOriginResolving] = useState(false);
  const [destinationResolving, setDestinationResolving] = useState(false);
  /**
   * Sube cada vez que el formulario reemplaza los puntos SIN que los campos
   * se remonten: "Limpiar" e invertir origen y destino. Los dos casos dejan
   * al campo con estado de un punto que ya no es el suyo —una resolución en
   * vuelo que repoblaría lo que el asesor borró, o el fallo de una dirección
   * que ahora está en el otro extremo—, y esto lo descarta. Ver `resetKey`
   * en `TransferPointAutocomplete`.
   */
  const [pointResetGeneration, setPointResetGeneration] = useState(0);
  const discardPointAttempts = () =>
    setPointResetGeneration((generation) => generation + 1);
  /**
   * Hay un punto resolviéndose. UNA derivación: bloquea invertir origen y
   * destino (escribiría las coordenadas en el extremo equivocado) y bloquea
   * buscar (la búsqueda saldría con el punto que el asesor acaba de
   * reemplazar).
   */
  const anyPointResolving = originResolving || destinationResolving;
  const [date, setDate] = useState(() => url.params.fecha ?? '');
  const [originTime, setOriginTime] = useState(() => url.params.hora ?? '10:00');
  const [endDate, setEndDate] = useState(() => url.params.fechaVuelta ?? '');
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [destinationTime, setDestinationTime] = useState(
    () => url.params.horaVuelta ?? '15:00'
  );
  const [passengers, setPassengers] = useState(
    () => transferPassengersFromParams(url.params) ?? [],
  );
  const { partySuggestion, consumePartySuggestion } = useBookingCaseContext();
  const [luggage, setLuggage] = useState(() => transferLuggageFromParams(url.params));
  useEffect(() => {
    if (!partySuggestion) return;
    setPassengers(
      transferPassengersFromParams({ pax: String(partySuggestion.size) }) ?? [],
    );
    consumePartySuggestion(partySuggestion.revision);
  }, [partySuggestion, consumePartySuggestion]);
  const [bookingQuote, setBookingQuote] = useState<
    SelectedTravelQuote<PricedSearchResult> | null
  >(null);
  const bookingOffer = bookingQuote?.offer ?? null;
  const [filters, setFilters] = useState<TransferFilters>(() => ({
    ...EMPTY_TRANSFER_FILTERS,
    providers: listParam(url.params, 'fProveedor'),
    categories: listParam(url.params, 'fVehiculo'),
    minCapacity: numParam(url.params, 'fPax', 0),
    instantOnly: url.params.fInmediata === '1',
    freeCancellationOnly: url.params.fCancela === '1',
  }));
  const [sort, setSort] = useState<TransferSort>(() =>
    enumParam(url.params, 'orden', ['price', 'capacity'] as const, 'price'));

  const isRoundTrip = tripType === 'ROUNDTRIP';

  /**
   * Lo que se busca sale de la URL, no del formulario en edición: así un deep
   * link con `buscar=1` reproduce exactamente la misma búsqueda, y editar el
   * formulario no re-dispara nada hasta apretar Buscar.
   */
  const searchPayload = useMemo(
    () => payloadFromParams(url.params),
    [url.params],
  );
  const search = useSearchTransfers(searchPayload);
  /** Vigencia de la generación que se está mostrando (§ barra de resumen). */
  const searchCountdown = useFareCountdown(
    search.data ? search.dataUpdatedAt : undefined,
  );
  const activeSearchIdentity = searchPayload
    ? quoteSearchIdentity('transfers', CONFIG.AGENCY_ID, searchPayload)
    : undefined;
  const bookingContext = transferCommittedBookingContext(searchPayload);
  /**
   * El `plc_*` de cada punto COMITEADO (no del formulario vivo): es lo único
   * que se manda a resolver, así que el mapa nunca puede terminar dibujando
   * la coordenada de un punto que el asesor eligió después de buscar.
   */
  const committedOriginPlaceId =
    bookingContext?.origin.kind === 'place' ? bookingContext.origin.place : undefined;
  const committedDestinationPlaceId =
    bookingContext?.destination.kind === 'place'
      ? bookingContext.destination.place
      : undefined;

  const typeLabel = (type: TransferPointType) =>
    ({
      A: t('travelServices.transfers.airport'),
      H: t('travelServices.transfers.hotel'),
      S: t('travelServices.transfers.station'),
      P: t('travelServices.transfers.port'),
    })[type];

  /**
   * `placeName` viene de la URL (`${prefix}iataNombre`), NUNCA del payload:
   * `kind: 'place'` se manda al hub como `{kind, place}` únicamente (el
   * contrato de rutero es `additionalProperties: false` por rama — un
   * `name` viajando en el JSON real haría rechazar el request entero), así
   * que el nombre para mostrar no puede ir ahí. Mismo patrón que
   * `destinoNombre` en tours (#2175): el nombre "amigable" vive aparte del
   * payload, solo para display.
   */
  const pointLabel = (point: TransferPointPayload, placeName?: string) =>
    point.place
      ? (placeName ?? point.place)
      : point.iata
        ? `${typeLabel(point.type ?? 'A')} ${point.iata.toUpperCase()}`
        : (() => {
          const name = point.name ?? point.ref ?? '';
          const address = point.address ?? '';
          if (!address) return name;
          if (!name) return address;
          return address.toLocaleLowerCase().startsWith(name.toLocaleLowerCase())
            ? address
            : `${name}, ${address}`;
        })();

  const formatDate = (iso: string) => iso.split('-').reverse().join('/');

  const results = (search.data?.results ?? []) as TransferOffer[];
  const providers = useMemo(
    () => [...new Set(results.map((offer) => offer.provider))].sort(),
    [results],
  );
  const categories = useMemo(
    () => [...new Set(results.map(categoryKey).filter(Boolean))].sort(),
    [results],
  );
  /** Ofertas por proveedor: el strip dice quién contestó y con cuánto. */
  const countsByProvider = useMemo(
    () =>
      results.reduce<Record<string, number>>((acc, offer) => {
        acc[offer.provider] = (acc[offer.provider] ?? 0) + 1;
        return acc;
      }, {}),
    [results],
  );

  const facetCount = (patch: Partial<TransferFilters>) =>
    transferFacetCount(results, filters, patch);

  const visible = useMemo(
    () => sortTransfers(filterTransfers(results, filters), sort),
    [results, filters, sort],
  );

  const activeFilterCount =
    filters.providers.length +
    filters.categories.length +
    (filters.minCapacity ? 1 : 0) +
    (filters.instantOnly ? 1 : 0) +
    (filters.freeCancellationOnly ? 1 : 0);

  const toggleInList = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  /**
   * ¿La ruta que se está armando toca un aeropuerto? De eso depende si la
   * hora que se pide es la del VUELO o la del servicio. Se mira lo que va a
   * VIAJAR (`toPayload`) más el tipo del lugar canónico, que el pedido no
   * lleva y el formulario sí conoce.
   */
  const endOf = (point: PointState) => {
    const payload = toPayload(point);
    return transferDraftRouteEnd({
      ...(payload.kind === 'detail' && payload.type ? { type: payload.type } : {}),
      hasPlace: Boolean(point.placePoint),
      ...(point.placeKind ? { placeKind: point.placeKind } : {}),
    });
  };
  const draftTouchesAirport = transferRouteTouchesAirport([
    endOf(origin),
    endOf(destination),
  ]);
  const timeLabelKey = (leg: 'origin' | 'destination') => {
    const base = leg === 'origin' ? 'originTime' : 'destinationTime';
    return `travelServices.transfers.${base}${draftTouchesAirport ? 'Flight' : ''}`;
  };

  const originPlaceName = url.params[transferPointNameParam('o')];
  const destinationPlaceName = url.params[transferPointNameParam('d')];
  /**
   * El resumen recapitula lo que se PIDIÓ, y lo que se pide es la hora del
   * vuelo: sin rotularla, un "10:00 hs" suelto se lee como la hora a la que
   * pasan a buscar al pasajero — que con dos de los tres conectores puede
   * estar cuatro horas después. La recogida real la dice cada oferta.
   */
  /**
   * La ruta COMPROMETIDA sale de `url.params`, que es de donde sale el propio
   * `searchPayload`: mismo origen que el borrador, así un aeropuerto canónico
   * comiteado sigue rotulándose como vuelo en el resumen.
   */
  const committedTouchesAirport = bookingContext
    ? transferRouteTouchesAirport(
      (['o', 'd'] as const).map((prefix) => {
        const point = prefix === 'o' ? bookingContext.origin : bookingContext.destination;
        return {
          ...(point && point.kind === 'detail' && point.type ? { type: point.type } : {}),
          ...(() => {
            const kind = transferPointKindFromParams(
              url.params[transferPointKindParam(prefix)],
            );
            return kind ? { placeKind: kind } : {};
          })(),
        };
      }),
    )
    : false;
  const requestedRoutes = bookingContext
    ? {
      outbound: {
        from: pointLabel(bookingContext.origin, originPlaceName),
        to: pointLabel(bookingContext.destination, destinationPlaceName),
      },
      ...(bookingContext.tripType === 'ROUNDTRIP'
        ? {
          return: {
            from: pointLabel(bookingContext.destination, destinationPlaceName),
            to: pointLabel(bookingContext.origin, originPlaceName),
          },
        }
        : {}),
    }
    : undefined;

  /**
   * Los puntos del PEDIDO para el mapa: los mismos que arma el resumen de
   * arriba, con el mismo nombre para mostrar. El proveedor puede haber
   * resuelto otros puntos — el mapa y la card los toman de
   * `originPoint`/`destinationPoint`, nunca de la prosa de `name`.
   *
   * Un punto elegido del maestro de lugares viaja al pedido como `plc_*` y
   * nada más: sus coordenadas se resuelven aparte, por ese id, y se indexan
   * POR ese id (#2247). La selección viva del formulario es solo un atajo —
   * ver `placeCoords.ts`.
   */
  const originPlaceCoords = usePlaceCoords(committedOriginPlaceId);
  const destinationPlaceCoords = usePlaceCoords(committedDestinationPlaceId);
  const bookingPlaceCoords = useMemo(
    () =>
      committedPlaceCoordsIndex([
        {
          id: committedOriginPlaceId,
          selection: origin.placePoint,
          resolved: originPlaceCoords.data,
        },
        {
          id: committedDestinationPlaceId,
          selection: destination.placePoint,
          resolved: destinationPlaceCoords.data,
        },
      ]),
    [
      committedOriginPlaceId,
      committedDestinationPlaceId,
      origin.placePoint,
      destination.placePoint,
      originPlaceCoords.data,
      destinationPlaceCoords.data,
    ],
  );
  const bookingRoutePoints = bookingContext
    ? transferRouteMapPoints(
      bookingContext,
      {
        origin: pointLabel(bookingContext.origin, originPlaceName),
        destination: pointLabel(bookingContext.destination, destinationPlaceName),
      },
      bookingPlaceCoords,
    )
    : [];

  const resolvedRoute = bookingOffer ? transferRoute(bookingOffer) : null;
  const transferSummary = bookingContext
    ? {
      kind: 'transfer' as const,
      ...(bookingContext.luggage ? { requestedLuggage: bookingContext.luggage } : {}),
      legs: [
        {
          direction: 'outbound' as const,
          date: formatDate(bookingContext.date),
          time: bookingContext.pickupTime,
          timeKind: committedTouchesAirport ? 'flight' as const : 'service' as const,
          from: pointLabel(bookingContext.origin, originPlaceName),
          to: pointLabel(bookingContext.destination, destinationPlaceName),
        },
        ...(bookingContext.tripType === 'ROUNDTRIP' && bookingContext.endDate
          ? [{
            direction: 'return' as const,
            date: formatDate(bookingContext.endDate),
            time: bookingContext.returnPickupTime,
            timeKind: committedTouchesAirport ? 'flight' as const : 'service' as const,
            from: pointLabel(bookingContext.destination, destinationPlaceName),
            to: pointLabel(bookingContext.origin, originPlaceName),
          }]
          : []),
      ],
      ...(resolvedRoute ? { resolvedRoute } : {}),
    }
    : undefined;
  const committedSearchSummary = transferSummary?.legs.map((leg) => [
    leg.date,
    leg.time,
    `${leg.from} → ${leg.to}`,
  ].filter(Boolean).join(' · ')) ?? [];
  const luggageSummary = transferLuggageSummary(bookingContext?.luggage, t);
  if (luggageSummary) committedSearchSummary.push(`${t('travelServices.transfers.luggage.requested')}: ${luggageSummary}`);

  /**
   * Validación de fechas del borrador — la MISMA cota que rechaza el DTO
   * compartido (`ruteroMinTransferPickupDate`: hoy en UTC−12, el criterio del
   * hub). El `min` de los inputs, el error de cada campo y el gate del submit
   * salen de esta ÚNICA derivación: el formulario nunca exige algo que no
   * marque dónde corregirlo.
   */
  const schedule = transferScheduleValidation({ date, endDate, isRoundTrip });
  const passengerValidation = validateSearchPassengers(
    passengers,
    TRANSFER_PASSENGER_RULES,
  );

  const canSearch =
    Boolean(date) &&
    schedule.ok &&
    transferPointsReady({
      originValid: isPointValid(origin),
      destinationValid: isPointValid(destination),
      anyPointResolving,
    }) &&
    passengerValidation.valid &&
    transferLuggageSelection(luggage) !== null &&
    (!isRoundTrip || Boolean(endDate));

  /**
   * El formulario, tal como se escribe en la URL. Es la ÚNICA derivación del
   * borrador: de acá salen la búsqueda que se confirma y el payload contra el
   * que se compara la comprometida (21.22), no dos expresiones parecidas.
   */
  const formParams: UrlParams = {
    buscar: '1',
    viaje: tripType,
    ...pointToParams(origin, 'o'),
    ...pointToParams(destination, 'd'),
    fecha: date,
    hora: originTime,
    fechaVuelta: isRoundTrip ? endDate : undefined,
    horaVuelta: isRoundTrip ? destinationTime : undefined,
    pax: searchPassengersToParam(passengers),
    ...transferLuggageToParams(luggage),
    // Se retiran al confirmar una búsqueda nueva, pero se siguen leyendo para
    // que los deep links anteriores al selector unificado sean reproducibles.
    adultos: undefined,
    menores: undefined,
  };
  const draftPayload = payloadFromParams(cleanParams(formParams));
  const formDiverged = searchFormDiverged(searchPayload, draftPayload);
  const resultAuthority = useSearchResultAuthority(search, formDiverged);

  /**
   * Rechazo del hub por fechas (`PICKUP_IN_PAST` / `RETURN_BEFORE_DEPARTURE`).
   * El borde propio valida con la misma cota, así que esto solo llega en una
   * carrera de relojes alrededor del corte — no es una falla del sistema: se
   * suprime el error genérico de búsqueda y se marca el CAMPO que lo causó,
   * mientras el formulario siga mostrando la búsqueda rechazada. Editado el
   * formulario (`formDiverged`), la marca describiría fechas que ya no se
   * ven y se retira; el asesor ya está armando otra búsqueda.
   */
  const showPickupHubError = isPickupInPast(search.error) && !formDiverged;
  const showReturnHubError =
    isReturnBeforeDeparture(search.error) && !formDiverged;

  // El formulario NO se escribe en la URL mientras se edita: si lo hiciera, la
  // búsqueda se re-dispararía con cada tecla (los params SON la query). La URL
  // se actualiza al confirmar — abajo, en `handleSearch`.
  //
  // Los filtros y el orden SÍ viajan en vivo: un link compartido muestra la misma
  // vista refinada que ve quien lo mandó.
  useEffect(() => {
    url.update({
      fProveedor: serializeList(filters.providers),
      fVehiculo: serializeList(filters.categories),
      fPax: filters.minCapacity ? String(filters.minCapacity) : undefined,
      fInmediata: filters.instantOnly ? '1' : undefined,
      fCancela: filters.freeCancellationOnly ? '1' : undefined,
      orden: sort === 'price' ? undefined : sort,
    });
    // `url.update` es estable (useCallback) y solo interesa reaccionar al
    // refinamiento, no a la identidad del hook.
  }, [filters, sort, url]);

  /** Vacía la búsqueda: campos, filtros, URL y memoria de navegación. */
  const handleClear = () => {
    discardPointAttempts();
    setTripType('ONEWAYTRIP');
    setOrigin(pointFromParams({}, 'o', 'iata'));
    setDestination(pointFromParams({}, 'd', 'coords'));
    setDate('');
    setOriginTime('10:00');
    setEndDate('');
    setDestinationTime('15:00');
    setPassengers(transferPassengersFromParams({}) ?? []);
    setLuggage(emptyTransferLuggage());
    setFilters(EMPTY_TRANSFER_FILTERS);
    url.reset();
  };

  /**
   * Confirmar la búsqueda es escribirla en la URL con `buscar=1`: eso crea la
   * entrada de historial y habilita la query. Sin ese flag la URL solo precarga
   * el formulario (params a medio cargar no disparan búsquedas).
   */
  const handleSearch = () => {
    const nextIdentity = draftPayload
      ? quoteSearchIdentity('transfers', CONFIG.AGENCY_ID, draftPayload)
      : undefined;
    if (
      shouldRefetchExpiredIdenticalSearch(
        activeSearchIdentity,
        nextIdentity,
        Boolean(search.data),
        search.dataUpdatedAt,
        Date.now(),
      )
    ) {
      void search.refetch();
    }
    url.commit(formParams);
  };

  /**
   * Re-cotización desde el modal (cambio de horario u oferta vencida). El
   * flujo entero — traer generación nueva, commitearla como búsqueda activa
   * SIEMPRE, sustituir el quote solo con match único — vive en
   * `runTransferRequote` (transfers.ts), que es donde está testeado; acá solo
   * se inyectan los efectos reales.
   *
   * `currentOffer` es la oferta VIGENTE del modal — la última re-cotizada si
   * ya hubo una, no la selección original del formulario: un segundo re-quote
   * se ata a lo que el asesor tiene delante.
   */
  const handleRequote = async (
    currentOffer: PricedSearchResult,
    newTime: string,
    newReturnTime?: string,
  ): Promise<
    SelectedTravelQuote<PricedSearchResult> | 'not_found' | 'ambiguous'
  > => {
    if (!searchPayload) return 'not_found';
    return runTransferRequote(
      searchPayload,
      currentOffer,
      CONFIG.AGENCY_ID,
      newTime,
      newReturnTime,
      {
        fetchGeneration: async (payload) => {
          // `fetchQuery` siembra el MISMO cache que la búsqueda: la
          // generación nueva es la que el listado va a mostrar.
          const queryKey = ruteroSearchKey('transfers', payload);
          const envelope = await queryClient.fetchQuery<SearchEnvelope>({
            queryKey,
            queryFn: async () => {
              const response = await client.post<SearchEnvelope>(
                'rutero/transfers/search',
                payload,
              );
              return response.data;
            },
            // EXPLÍCITO a propósito: un re-quote existe para traer una
            // generación NUEVA (precio vigente, provenance nuevo y — clave en
            // el camino de oferta vencida — un `expiresAt` nuevo). Servir la
            // generación cacheada reinstalaría la MISMA provenance muerta y
            // el próximo hold volvería a `OFFER_EXPIRED`, en loop. Hoy el
            // default del QueryClient ya es 0 (verificado contra react-query
            // 3.39.3: el `staleTime` del observer del buscador NO contamina a
            // `fetchQuery`), pero este invariante es de un camino con plata y
            // no puede depender de que nadie configure un
            // `defaultOptions.queries.staleTime` global mañana.
            staleTime: 0,
          });
          return {
            envelope,
            dataUpdatedAt:
              queryClient.getQueryState<SearchEnvelope>(queryKey)?.dataUpdatedAt,
          };
        },
        // La generación traída pasa a ser la búsqueda ACTIVA, con o sin
        // sustitución: horarios del form + URL (de la que salen el payload
        // commiteado, la query del listado y la identidad de búsqueda).
        commitSchedule: (payload) => {
          setOriginTime(payload.originTime);
          if (payload.tripType === 'ROUNDTRIP' && payload.destinationTime) {
            setDestinationTime(payload.destinationTime);
          }
          url.update({
            hora: payload.originTime,
            ...(payload.tripType === 'ROUNDTRIP'
              ? { horaVuelta: payload.destinationTime }
              : {}),
          });
        },
      },
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* Un traslado necesita más datos que los otros verticales (cada punto
          tiene tipo y forma de ubicarlo), pero eso no lo hace otro formulario:
          va todo en la MISMA barra, en bloques titulados. */}
      <SearchBar
        onClear={url.hasParams || luggage.mode !== 'later' ? handleClear : undefined}
        onSearch={handleSearch}
        loading={search.isLoading}
        disabled={!canSearch}
        sticky={false}
      >
        <SearchRow>
          <PointFields
            label={t('travelServices.transfers.origin')}
            point={origin}
            onChange={setOrigin}
            placesEnabled={placesEnabled}
            onResolvingChange={setOriginResolving}
            resetKey={pointResetGeneration}
          />
          <PointFields
            label={t('travelServices.transfers.destination')}
            point={destination}
            onChange={setDestination}
            placesEnabled={placesEnabled}
            onResolvingChange={setDestinationResolving}
            resetKey={pointResetGeneration}
            action={
              // Bloqueado mientras cualquiera de los dos puntos esté
              // resolviendo: cada `TransferPointAutocomplete` vive en una
              // posición FIJA y su `onChange` apunta siempre al mismo extremo, así que
              // invertir a mitad de una resolución escribiría las coordenadas
              // en el punto equivocado — un traslado buscado para un lugar que
              // el asesor no eligió. El estado inconsistente no se corrige
              // después: se hace inalcanzable (la ventana es el roundtrip del
              // resolve, unos cientos de ms).
              <Tooltip
                title={t(
                  anyPointResolving
                    ? 'travelServices.transfers.swapWhileResolving'
                    : 'travelServices.transfers.swap',
                )}
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={anyPointResolving}
                    onClick={() => {
                      // Los campos NO se remontan al invertir: cada uno se
                      // queda con el estado de resolución de su POSICIÓN, y un
                      // fallo de dirección seguiría mostrándose —con su salida
                      // a carga manual— sobre el punto que ahora es el otro.
                      discardPointAttempts();
                      setOrigin(destination);
                      setDestination(origin);
                    }}
                  >
                    <SwapVert fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            }
          />
        </SearchRow>
        <SearchRow>
          <SearchField label={t('travelServices.transfers.tripType')} width={160}>
            <TextField
              select
              size="small"
              label={t('travelServices.transfers.tripType')}
              value={tripType}
              onChange={(event) =>
                setTripType(event.target.value as 'ONEWAYTRIP' | 'ROUNDTRIP')
              }
            >
              <MenuItem value="ONEWAYTRIP">
                {t('travelServices.transfers.oneWay')}
              </MenuItem>
              <MenuItem value="ROUNDTRIP">
                {t('travelServices.transfers.roundTrip')}
              </MenuItem>
            </TextField>
          </SearchField>
          <SearchDivider />
          <SearchField
            label={t('travelServices.transfers.date')}
            width={SEARCH_DATE_FIELD_WIDTH}
            xsShare={SEARCH_FIELD_WIDE_SHARE}
          >
            <SearchDatePicker
              label={t('travelServices.transfers.date')}
              value={date}
              onChange={(nextDate) => {
                setDate(nextDate);
                if (isRoundTrip) {
                  setEndDate((current) => reconcileSearchEndDate(nextDate, current));
                }
              }}
              onAccept={() => {
                if (isRoundTrip) setEndDateOpen(true);
              }}
              required
              minDate={schedule.minPickupDate}
              error={schedule.pickupInPast || showPickupHubError}
              helperText={
                schedule.pickupInPast || showPickupHubError
                  ? t('travelServices.transfers.dateInPast')
                  : undefined
              }
            />
          </SearchField>
          <SearchField
            label={t(timeLabelKey('origin'))}
            width={135}
            xsShare={SEARCH_FIELD_NARROW_SHARE}
          >
            {/* La hora que se pide NO es la de recogida: dos de los tres
                conectores la leen como la del vuelo y calculan la recogida
                ellos. El rótulo lo dice y el tooltip explica el caso sin
                aeropuerto; la recogida real la muestra cada oferta. */}
            <Tooltip title={t('travelServices.transfers.flightTimeHint')}>
              <TextField
                label={t(timeLabelKey('origin'))}
                type="time"
                size="small"
                value={originTime}
                onChange={(event) => setOriginTime(event.target.value)}
              />
            </Tooltip>
          </SearchField>
          {isRoundTrip && <SearchDivider />}
          {isRoundTrip && (
            <SearchField
              label={t('travelServices.transfers.endDate')}
              width={SEARCH_DATE_FIELD_WIDTH}
              xsShare={SEARCH_FIELD_WIDE_SHARE}
            >
              <SearchDatePicker
                label={t('travelServices.transfers.endDate')}
                value={endDate}
                onChange={setEndDate}
                minDate={schedule.minReturnDate}
                open={endDateOpen}
                onOpen={() => setEndDateOpen(true)}
                onClose={() => setEndDateOpen(false)}
                required
                error={
                  schedule.returnBeforePickup ||
                  schedule.returnInPast ||
                  showReturnHubError
                }
                helperText={
                  schedule.returnBeforePickup || showReturnHubError
                    ? t('travelServices.transfers.returnBeforePickup')
                    : schedule.returnInPast
                      ? t('travelServices.transfers.dateInPast')
                      : undefined
                }
              />
            </SearchField>
          )}
          {isRoundTrip && (
            <SearchField
              label={t(timeLabelKey('destination'))}
              width={135}
              xsShare={SEARCH_FIELD_NARROW_SHARE}
            >
              <TextField
                label={t(timeLabelKey('destination'))}
                type="time"
                size="small"
                value={destinationTime}
                onChange={(event) => setDestinationTime(event.target.value)}
              />
            </SearchField>
          )}
          <SearchDivider />
          <SearchField
            label={t('travelServices.passengerPicker.label')}
            width={SEARCH_PASSENGER_FIELD_WIDTH}
          >
            <SearchPassengerPicker
              value={passengers}
              onChange={setPassengers}
              rules={TRANSFER_PASSENGER_RULES}
            />
          </SearchField>
        </SearchRow>
        <TransferLuggagePicker value={luggage} onChange={setLuggage} />
      </SearchBar>

      {url.params.buscar !== '1' && (
        <RecentSearches
          vertical="transfers"
          describe={(params) => describeTransfers(params, t, i18n.language)}
        />
      )}

      {/* La URL pide una búsqueda que no se puede ejecutar (link
          recortado o editado a mano): decirlo, no quedarse mudo. */}
      {isIncompleteSearch(url.params, searchPayload) && (
        <Alert severity="warning">{t('travelServices.incompleteLink')}</Alert>
      )}

      <SearchResultGenerationStatus authority={resultAuthority} />

      {url.params.buscar === '1' && searchPayload && (
        <ActiveSearchSummary
          label={t('travelServices.activeSearchSummary')}
          lines={committedSearchSummary}
          countdown={searchCountdown}
          onRefresh={() => void search.refetch()}
        />
      )}

      {/* El recorrido de la búsqueda comprometida, uno por búsqueda y
          colapsado: el iframe no existe hasta que el asesor lo abre. La
          identidad de la búsqueda va de `key` para que una búsqueda nueva
          arranque cerrada y sin el mapa de la anterior. */}
      {url.params.buscar === '1' && searchPayload && (
        <SearchRouteMap
          key={searchRouteMapKey(activeSearchIdentity, bookingRoutePoints)}
          vertical="transfers"
          points={bookingRoutePoints}
        />
      )}

      {search.isLoading && <ResultsSkeleton archetype="product" />}
      {/* Un rechazo por fecha del hub NO es una falla de búsqueda: mientras
          está marcado en su campo (arriba), repetirlo acá como error genérico
          diría "algo se rompió" cuando lo que hay es una fecha para corregir.
          Si el formulario ya divergió la marca de campo se retira y el error
          genérico vuelve a describir la búsqueda comprometida — el rechazo
          nunca queda invisible. */}
      {search.isError && !showPickupHubError && !showReturnHubError && (
        <Alert severity="error">{t('travelServices.searchError')}</Alert>
      )}
      {search.data && !search.isLoading && (
        <ProviderStatusStrip
          providers={search.data.providers}
          countsByProvider={countsByProvider}
        />
      )}

      {search.data && !search.isLoading && results.length > 0 && (
        <ResultFiltersBar
          activeCount={activeFilterCount}
          onClear={() => setFilters(EMPTY_TRANSFER_FILTERS)}
          resultCount={visible.length}
          sort={{
            value: sort,
            onChange: (value) => setSort(value as TransferSort),
            options: [
              { value: 'price', label: t('travelServices.cars.sortPrice') },
              { value: 'capacity', label: t('travelServices.transfers.sortCapacity') },
            ],
          }}
        >
          {(stacked) => (
            <>
              <FilterGroup label={t('travelServices.transfers.providerFilter')} stacked={stacked}>
                {providers.map((provider) => (
                  <FilterChip
                    key={provider}
                    label={provider}
                    count={facetCount({ providers: [provider] })}
                    active={filters.providers.includes(provider)}
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        providers: toggleInList(prev.providers, provider),
                      }))
                    }
                  />
                ))}
              </FilterGroup>
              <FilterGroup label={t('travelServices.transfers.categoryFilter')} stacked={stacked}>
                {categories.map((category) => (
                  <FilterChip
                    key={category}
                    label={categoryLabel(category)}
                    count={facetCount({ categories: [category] })}
                    active={filters.categories.includes(category)}
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        categories: toggleInList(prev.categories, category),
                      }))
                    }
                  />
                ))}
              </FilterGroup>
              <FilterGroup label={t('travelServices.transfers.capacityFilter')} stacked={stacked}>
                {CAPACITY_FILTERS.map((capacity) => (
                  <FilterChip
                    key={capacity}
                    label={`${capacity}+`}
                    count={facetCount({ minCapacity: capacity })}
                    active={filters.minCapacity === capacity}
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        minCapacity: prev.minCapacity === capacity ? 0 : capacity,
                      }))
                    }
                  />
                ))}
              </FilterGroup>
              <FilterGroup label={t('travelServices.transfers.conditionsFilter')} stacked={stacked}>
                <FilterChip
                  label={t('travelServices.transfers.instant')}
                  count={facetCount({ instantOnly: true })}
                  active={filters.instantOnly}
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, instantOnly: !prev.instantOnly }))
                  }
                />
                <FilterChip
                  label={t('travelServices.transfers.freeCancellationFilter')}
                  count={facetCount({ freeCancellationOnly: true })}
                  active={filters.freeCancellationOnly}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      freeCancellationOnly: !prev.freeCancellationOnly,
                    }))
                  }
                />
              </FilterGroup>
            </>
          )}
        </ResultFiltersBar>
      )}

      {search.data && !search.isLoading && (
        <>
          <Typography variant="body2" color="text.secondary">
            {t('travelServices.transfers.found', {
              count: visible.length,
              total: results.length,
            })}
          </Typography>
          {visible.length === 0 ? (
            <Alert severity="info">{t('travelServices.noResults')}</Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {visible.map((offer, index) => (
                <TransferOfferCard
                  key={transferOfferListKey(offer)}
                  offer={offer}
                  index={index}
                  // Las horas del payload COMPROMETIDO (el que produjo estas
                  // ofertas), no las del formulario en curso: §21.23.4.
                  requestedTimes={{
                    ...(searchPayload?.originTime
                      ? { outbound: searchPayload.originTime }
                      : {}),
                    ...(searchPayload?.destinationTime
                      ? { return: searchPayload.destinationTime }
                      : {}),
                  }}
                  requestedRoutes={requestedRoutes}
                  isRoundTrip={searchPayload?.tripType === 'ROUNDTRIP'}
                  bookingDisabled={!resultAuthority.canSelectResult}
                  onBook={(selectedOffer) =>
                    setBookingQuote(
                      captureSelectableSearchQuote(
                        selectedOffer,
                        search.dataUpdatedAt,
                        'transfers',
                        CONFIG.AGENCY_ID,
                        searchPayload,
                        Date.now(),
                        formDiverged,
                      ),
                    )
                  }
                />
              ))}
            </Box>
          )}
        </>
      )}

      <BookingModal
        vertical="transfers"
        quote={bookingQuote}
        activeSearchIdentity={activeSearchIdentity}
        transferSummary={transferSummary}
        routePoints={bookingRoutePoints}
        pickupTime={bookingContext?.pickupTime}
        returnPickupTime={bookingContext?.returnPickupTime}
        routeTouchesAirport={committedTouchesAirport}
        isRoundTrip={bookingContext?.tripType === 'ROUNDTRIP'}
        onRequote={handleRequote}
        party={committedParty({ vertical: 'transfers', payload: searchPayload })}
        onClose={() => setBookingQuote(null)}
      />
    </Box>
  );
}
